// CB:BEGIN admin-verification-queue-page
import React, { useState, useEffect } from 'react';
import { useApi } from '../../lib/useApi';

interface QueueRow {
  contractorId: string;
  businessName: string | null;
  businessAddress: string | null;
  licenseNumber: string | null;
  licenseState: string | null;
  licenseExpiresAt: string | null;
  generalLiabilityExpiresAt: string | null;
  submittedAt: string | null;
  email: string | null;
}

interface DocRow {
  id: string;
  type: string;
  fileName: string | null;
  createdAt: string;
}

const AdminVerificationQueuePage: React.FC<{ onNavigate?: (screen: string) => void }> = ({ onNavigate }) => {
  const api = useApi();
  const [queue, setQueue] = useState<QueueRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState<{ contractor: { id: string; company?: string; email?: string }; verification: Record<string, unknown>; documents: DocRow[] } | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [actioning, setActioning] = useState<string | null>(null);

  const fetchQueue = async () => {
    try {
      const d = await api.get<{ queue: QueueRow[] }>('/api/admin/verification-queue');
      setQueue(d.queue || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQueue();
  }, []);

  const openDetail = async (contractorId: string) => {
    try {
      const d = await api.get<typeof detail>(`/api/admin/verification-queue/${contractorId}`);
      setDetail(d);
      setRejectReason('');
    } catch (e) {
      console.error(e);
    }
  };

  const approve = async (contractorId: string) => {
    setActioning(contractorId);
    try {
      await api.post(`/api/admin/verification-queue/${contractorId}/approve`);
      setDetail(null);
      await fetchQueue();
    } catch (e) {
      console.error(e);
    } finally {
      setActioning(null);
    }
  };

  const reject = async (contractorId: string) => {
    setActioning(contractorId);
    try {
      await api.post(`/api/admin/verification-queue/${contractorId}/reject`, { reason: rejectReason || 'No reason provided' });
      setDetail(null);
      await fetchQueue();
    } catch (e) {
      console.error(e);
    } finally {
      setActioning(null);
    }
  };

  const getDownloadUrl = async (contractorId: string, docId: string) => {
    try {
      const d = await api.get<{ url: string }>(`/api/admin/verification-queue/${contractorId}/documents/${docId}/download`);
      if (d.url) window.open(d.url, '_blank');
    } catch (e) {
      console.error('Failed to get download URL', e);
    }
  };

  if (loading) return <div style={{ padding: 24 }}>Loading queue…</div>;

  return (
    <div style={{ padding: 24 }}>
      <h1 style={{ marginBottom: 24 }}>Verification queue</h1>
      {queue.length === 0 ? (
        <p style={{ color: '#6b7280' }}>No submissions pending review.</p>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid #e5e7eb', textAlign: 'left' }}>
              <th style={{ padding: 8 }}>Business / Email</th>
              <th style={{ padding: 8 }}>Submitted</th>
              <th style={{ padding: 8 }}>License exp</th>
              <th style={{ padding: 8 }}>Insurance exp</th>
              <th style={{ padding: 8 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {queue.map((row) => (
              <tr key={row.contractorId} style={{ borderBottom: '1px solid #e5e7eb' }}>
                <td style={{ padding: 8 }}>{row.businessName || row.email || row.contractorId}</td>
                <td style={{ padding: 8 }}>{row.submittedAt ? new Date(row.submittedAt).toLocaleDateString() : '—'}</td>
                <td style={{ padding: 8 }}>{row.licenseExpiresAt ? new Date(row.licenseExpiresAt).toLocaleDateString() : '—'}</td>
                <td style={{ padding: 8 }}>{row.generalLiabilityExpiresAt ? new Date(row.generalLiabilityExpiresAt).toLocaleDateString() : '—'}</td>
                <td style={{ padding: 8 }}>
                  <button type="button" className="btn btn-secondary" style={{ marginRight: 8 }} onClick={() => openDetail(row.contractorId)}>View</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {detail && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 24, maxWidth: 640, maxHeight: '90vh', overflow: 'auto' }}>
            <h2 style={{ marginBottom: 16 }}>{detail.contractor.company || detail.contractor.email || detail.contractor.id}</h2>
            <p><strong>Email:</strong> {detail.contractor.email}</p>
            <p><strong>Business name:</strong> {(detail.verification as any)?.business_name || (detail.verification as any)?.legalBusinessName || '—'}</p>
            {(detail.verification as any)?.dbaName && <p><strong>DBA:</strong> {(detail.verification as any).dbaName}</p>}
            <p><strong>Address:</strong> {(detail.verification as any)?.business_address || '—'}</p>
            <p><strong>License:</strong> {(detail.verification as any)?.license_number || '—'} ({(detail.verification as any)?.licenseState || '—'})</p>
            {((detail.verification as any)?.ein || (detail.verification as any)?.taxClassification) && (
              <p><strong>EIN / Tax class:</strong> {(detail.verification as any)?.ein || '—'} / {(detail.verification as any)?.taxClassification || '—'}</p>
            )}
            {((detail.verification as any)?.generalLiabilityPolicyNumber || (detail.verification as any)?.workersCompPolicyNumber) && (
              <div style={{ marginTop: 8 }}>
                <strong>Insurance policy numbers</strong>
                {(detail.verification as any)?.generalLiabilityPolicyNumber && <p style={{ margin: '4px 0' }}>GL: {(detail.verification as any).generalLiabilityPolicyNumber}</p>}
                {(detail.verification as any)?.workersCompPolicyNumber && <p style={{ margin: '4px 0' }}>WC: {(detail.verification as any).workersCompPolicyNumber}</p>}
              </div>
            )}
            {((detail.verification as any)?.authorizedRepresentativeName || (detail.verification as any)?.authorizedRepresentativeEmail) && (
              <div style={{ marginTop: 8, padding: '8px 12px', background: '#f9fafb', borderRadius: 6 }}>
                <strong>Authorized representative</strong>
                {(detail.verification as any)?.authorizedRepresentativeName && <p style={{ margin: '4px 0' }}>{(detail.verification as any).authorizedRepresentativeName}{(detail.verification as any)?.authorizedRepresentativeTitle && ` — ${(detail.verification as any).authorizedRepresentativeTitle}`}</p>}
                {(detail.verification as any)?.authorizedRepresentativeEmail && <p style={{ margin: '4px 0' }}>{(detail.verification as any).authorizedRepresentativeEmail}</p>}
                {(detail.verification as any)?.authorizedRepresentativePhone && <p style={{ margin: '4px 0' }}>{(detail.verification as any).authorizedRepresentativePhone}</p>}
              </div>
            )}
            {((detail.verification as any)?.pocFirstName || (detail.verification as any)?.pocEmail) && (
              <div style={{ marginTop: 8, padding: '8px 12px', background: '#f9fafb', borderRadius: 6 }}>
                <strong>Point of contact</strong>
                <p style={{ margin: '4px 0' }}>{[(detail.verification as any)?.pocFirstName, (detail.verification as any)?.pocLastName].filter(Boolean).join(' ') || '—'}{(detail.verification as any)?.pocPosition && ` — ${(detail.verification as any).pocPosition}`}</p>
                {(detail.verification as any)?.pocEmail && <p style={{ margin: '4px 0' }}>{(detail.verification as any).pocEmail}</p>}
                {(detail.verification as any)?.pocPhone && <p style={{ margin: '4px 0' }}>{(detail.verification as any).pocPhone}</p>}
              </div>
            )}
            <div style={{ marginTop: 16 }}>
              <strong>Documents</strong>
              <ul style={{ marginTop: 8 }}>
                {detail.documents.map((doc) => (
                  <li key={doc.id}>
                    {doc.type} {doc.fileName && `— ${doc.fileName}`}
                    <button type="button" className="btn btn-secondary" style={{ marginLeft: 8 }} onClick={() => getDownloadUrl(detail.contractor.id, doc.id)}>View</button>
                  </li>
                ))}
              </ul>
            </div>
            <div style={{ marginTop: 16 }}>
              <label>Reject reason (required for reject)</label>
              <input type="text" className="form-input" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} style={{ width: '100%', padding: 8, marginTop: 4 }} placeholder="Reason for rejection" />
            </div>
            <div style={{ marginTop: 24, display: 'flex', gap: 8 }}>
              <button type="button" className="btn btn-primary" disabled={!!actioning} onClick={() => approve(detail.contractor.id)}>{actioning === detail.contractor.id ? '…' : 'Approve'}</button>
              <button type="button" className="btn btn-secondary" disabled={!!actioning} onClick={() => reject(detail.contractor.id)}>{actioning === detail.contractor.id ? '…' : 'Reject'}</button>
              <button type="button" className="btn btn-secondary" onClick={() => setDetail(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminVerificationQueuePage;
// CB:END admin-verification-queue-page
